import React from 'react';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Hero from "@/components/Hero";
import ServicesSection from "@/components/ServicesSection";
import ValocapSection from "@/components/ValocapSection";
import NumbersSection from "@/components/NumbersSection";
import EngagementCards from "@/components/EngagementCards";
import MapSection from "@/components/MapSection";
import InterventionMap from "@/components/map/intervention";
import GooglePlacesSummary from "@/components/GooglePlacesSummary";
import RecentReviews from "@/components/RecentReviews";
import MetalEstimationCTA from "@/components/MetalEstimationCTA";
import StructuredData from "@/components/StructuredData";
import { getOrganizationSchema, getLocalBusinessSchema } from "@/data/structuredData/organizationSchema";
import { getWebsiteSchema, getWebPageSchema } from "@/data/structuredData/websiteSchemas";
const Index = () => {
  // Données structurées multiples pour la page d'accueil
  const structuredDataArray = [getOrganizationSchema(), getLocalBusinessSchema(), getWebsiteSchema(), getWebPageSchema({
    url: "https://www.groupefabre-environnement.fr/",
    title: "Groupe Fabre Environnement - Recyclage et valorisation des déchets en Occitanie",
    description: "Expert en gestion, collecte et valorisation des déchets pour professionnels, collectivités et particuliers depuis plus de 25 ans en Occitanie.",
    datePublished: "2020-01-01T00:00:00+01:00",
    dateModified: new Date().toISOString()
  })];
  return <div className="flex flex-col min-h-screen">
      <SEO 
        title="Groupe Fabre Environnement - Recyclage et Valorisation des Déchets en Occitanie | Expert depuis 25+ ans" 
        description="✓ Expert recyclage Occitanie ✓ Location bennes ✓ Achat métaux ✓ Démolition ✓ Valorisation déchets ✓ 25+ ans expertise ✓ 8 départements ✓ Devis gratuit" 
        canonical="/" 
        keywords="recyclage Occitanie, déchets Rodez, valorisation déchets, location bennes Aveyron, achat métaux, Groupe Fabre Environnement, gestion déchets professionnels, économie circulaire" 
      />
      <script type="application/ld+json">
        {JSON.stringify(structuredDataArray)}
      </script>
      
      {/* Données structurées multiples */}
      <StructuredData data={structuredDataArray} />
      
      <Navbar />
      <main className="flex-grow pt-16 md:pt-20" id="main-content">
        {/* Hero Section with semantic role */}
        <section aria-labelledby="hero-heading">
          <Hero />
        </section>
        
        {/* Synthèse des avis Google */}
        <GooglePlacesSummary />
        
        {/* Avis clients récents */}
        <RecentReviews />
        
        {/* Introduction Section - Hero content */}
        <section aria-labelledby="introduction-heading" className="py-20 bg-gradient-to-br from-white via-fabre-lightgreen/20 to-fabre-beige/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-fabre-green/10 border border-fabre-green/20 rounded-full px-6 py-2 mb-8">
                <span className="w-2 h-2 bg-fabre-green rounded-full"></span>
                <span className="text-sm font-medium text-fabre-darkgreen">Expertise depuis 35 ans</span>
              </div>
              
              <h1 id="hero-heading" className="text-4xl md:text-6xl font-heading font-bold mb-8 text-fabre-gray leading-tight">
                Groupe Fabre Environnement
                <span className="block mt-2 bg-gradient-to-r from-fabre-green to-fabre-teal bg-clip-text text-transparent">
                  Expert en gestion des déchets
                </span>
              </h1>
              
              <p className="text-xl leading-relaxed text-fabre-darkteal mb-8 max-w-3xl mx-auto">
                Depuis plus de <strong className="text-fabre-green">35 ans</strong>, nous développons une approche responsable et innovante de la gestion des déchets pour nos clients professionnels, collectivités et particuliers en Occitanie.
              </p>
              
              {/* Stats en ligne */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 mb-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center border border-fabre-green/10 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="text-3xl font-bold text-fabre-green mb-2">35+</div>
                  <div className="text-sm text-fabre-darkteal font-medium">Années d'expertise</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center border border-fabre-teal/10 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="text-3xl font-bold text-fabre-teal mb-2">8</div>
                  <div className="text-sm text-fabre-darkteal font-medium">Départements couverts</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center border border-fabre-green/10 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="text-3xl font-bold text-fabre-green mb-2">100%</div>
                  <div className="text-sm text-fabre-darkteal font-medium">Éco-responsable</div>
                </div>
              </div>
              
              {/* Services clés */}
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-fabre-green/20 shadow-xl">
                <h2 id="introduction-heading" className="text-2xl font-heading font-bold mb-6 text-fabre-gray">
                  Nos services principaux
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-fabre-lightgreen/10 hover:bg-fabre-lightgreen/20 transition-colors">
                    <div className="text-2xl mb-2">🚛</div>
                    <div className="font-medium text-fabre-darkgreen">Location de bennes</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-fabre-teal/10 hover:bg-fabre-teal/20 transition-colors">
                    <div className="text-2xl mb-2">♻️</div>
                    <div className="font-medium text-fabre-darkteal">Achat de métaux</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-fabre-green/10 hover:bg-fabre-green/20 transition-colors">
                    <div className="text-2xl mb-2">🏗️</div>
                    <div className="font-medium text-fabre-darkgreen">Démolition</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-fabre-darkgreen/10 hover:bg-fabre-darkgreen/20 transition-colors">
                    <div className="text-2xl mb-2">🌱</div>
                    <div className="font-medium text-fabre-darkgreen">Valorisation</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-fabre-teal/10 hover:bg-fabre-teal/20 transition-colors">
                    <div className="text-2xl mb-2">🛢️</div>
                    <div className="font-medium text-fabre-darkteal">Collecte huiles minérales</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-fabre-green/10 hover:bg-fabre-green/20 transition-colors">
                    <div className="text-2xl mb-2">🫒</div>
                    <div className="font-medium text-fabre-darkgreen">Collecte huiles alimentaires</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Key Numbers Section */}
        <section aria-labelledby="numbers-heading" className="relative bg-gradient-to-r from-fabre-green via-fabre-teal to-fabre-darkgreen overflow-hidden">
          {/* Motifs décoratifs */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white rounded-full"></div>
            <div className="absolute top-20 right-20 w-16 h-16 border border-white rounded-lg rotate-45"></div>
            <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-white/20 rounded-full"></div>
            <div className="absolute bottom-10 right-1/3 w-8 h-8 bg-white/30 rounded-full"></div>
          </div>
          <NumbersSection />
        </section>
        
        {/* Services Overview Section */}
        <section aria-labelledby="services-heading" className="relative bg-gradient-to-br from-white via-fabre-lightgreen/20 to-fabre-beige/50 overflow-hidden">
          {/* Formes géométriques décoratives */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-fabre-green/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-fabre-teal/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-fabre-darkgreen/3 rounded-full blur-3xl"></div>
          <ServicesSection />
        </section>
        
        {/* Nouvelle section pour l'estimation des métaux */}
        <section aria-labelledby="metal-estimation-heading" className="relative bg-gradient-to-r from-fabre-lightgreen/40 via-fabre-beige/60 to-fabre-green/20 overflow-hidden">
          {/* Effets de lumière */}
          <div className="absolute top-0 left-1/4 w-32 h-32 bg-yellow-300/20 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-amber-300/20 rounded-full blur-2xl animate-pulse" style={{animationDelay: '0.5s'}}></div>
          <MetalEstimationCTA />
        </section>
        
        {/* Engagements Section */}
        <section aria-labelledby="engagements-heading" className="relative bg-gradient-to-br from-fabre-gray/5 via-white to-fabre-teal/10 overflow-hidden">
          {/* Motifs de recyclage subtils */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-20 text-6xl text-fabre-green rotate-12">♻</div>
            <div className="absolute top-40 right-32 text-4xl text-fabre-teal -rotate-12">🌱</div>
            <div className="absolute bottom-32 left-1/3 text-5xl text-fabre-darkgreen rotate-45">⚡</div>
            <div className="absolute bottom-20 right-20 text-4xl text-fabre-green -rotate-6">🌍</div>
          </div>
          <EngagementCards />
        </section>
        
        {/* Map Sections */}
        <section aria-labelledby="locations-heading" className="bg-gradient-to-br from-white to-fabre-lightgreen/10">
          <MapSection />
        </section>
        
        <section aria-labelledby="intervention-map-heading" className="relative bg-gradient-to-b from-fabre-lightgray/50 via-fabre-beige/30 to-white overflow-hidden">
          {/* Cercles concentriques pour représenter les zones d'intervention */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5">
            <div className="w-96 h-96 border-2 border-fabre-green rounded-full animate-pulse"></div>
            <div className="absolute top-8 left-8 w-80 h-80 border border-fabre-teal rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute top-16 left-16 w-64 h-64 border border-fabre-darkgreen rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          </div>
          <InterventionMap />
        </section>
        
        {/* Valocap Section */}
        <section aria-labelledby="valocap-heading" className="relative bg-gradient-to-r from-fabre-green/10 via-fabre-lightgreen/20 to-fabre-teal/10 overflow-hidden">
          {/* Éléments technologiques */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-6 h-6 border-2 border-fabre-green rounded"></div>
            <div className="absolute top-20 left-32 w-4 h-4 bg-fabre-teal rounded-full"></div>
            <div className="absolute top-32 left-20 w-8 h-8 border border-fabre-darkgreen rounded-full"></div>
            <div className="absolute bottom-20 right-20 w-6 h-6 border-2 border-fabre-green rounded rotate-45"></div>
            <div className="absolute bottom-32 right-40 w-4 h-4 bg-fabre-teal rounded"></div>
            <div className="absolute bottom-40 right-32 w-5 h-5 border border-fabre-darkgreen rounded-full"></div>
          </div>
          <ValocapSection />
        </section>
      </main>
      <Footer />
    </div>;
};
export default Index;